import React, { useEffect, useMemo, useState } from "react";
import { Authenticator } from "@aws-amplify/ui-react";
import { getCurrentUser } from "aws-amplify/auth";
import "./BrandedAuthenticator.scss";

const BLINGS_LOGO_URL =
  "https://assets.blings.io/brand/2026/Blings_Logo_White.svg";

type AuthRouteHeaderProps = {
  title: string;
  onTitleChange: (title: string) => void;
};

function AuthRouteHeader({ title, onTitleChange }: AuthRouteHeaderProps) {
  useEffect(() => {
    onTitleChange(title);
  }, [onTitleChange, title]);

  return null;
}

const AUTH_ROUTE_TITLES = {
  SignIn: "Sign in",
  SignUp: "Sign up",
  ConfirmSignUp: "Confirm sign up",
  ConfirmSignIn: "Confirm sign in",
  ForceNewPassword: "Change Password",
  ForgotPassword: "Reset password",
  ConfirmResetPassword: "Reset password",
  VerifyUser: "Account recovery",
  ConfirmVerifyUser: "Account recovery",
  SelectMfaType: "Select MFA type",
  SetupEmail: "Set up email",
  SetupTotp: "Set up authenticator",
};

const BrandedAuthenticator = (props: any) => {
  const [authCheckStatus, setAuthCheckStatus] = useState<
    "checking" | "signedIn" | "signedOut"
  >("checking");
  const [formTitle, setFormTitle] = useState("Sign in");
  const customComponents = props.components;

  useEffect(() => {
    let isMounted = true;

    getCurrentUser()
      .then(() => {
        if (isMounted) {
          setAuthCheckStatus("signedIn");
        }
      })
      .catch(() => {
        if (isMounted) {
          setAuthCheckStatus("signedOut");
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const components = useMemo(() => {
    const createHeader = (title: string) => {
      function RouteHeader() {
        return <AuthRouteHeader title={title} onTitleChange={setFormTitle} />;
      }

      return RouteHeader;
    };

    return {
      ...customComponents,
      ...Object.entries(AUTH_ROUTE_TITLES).reduce(
        (acc, [routeName, title]) => ({
          ...acc,
          [routeName]: {
            ...customComponents?.[routeName],
            Header: createHeader(title),
          },
        }),
        {}
      ),
    };
  }, [customComponents]);

  if (authCheckStatus === "signedIn") {
    return null;
  }

  return (
    <main className="BrandedAuthenticator" aria-label="Blings authentication">
      {authCheckStatus === "checking" ? (
        <div className="auth-session-check" role="status" aria-live="polite">
          <img className="auth-brand-logo" src={BLINGS_LOGO_URL} alt="Blings" />
          <div className="auth-session-spinner" aria-hidden="true" />
        </div>
      ) : (
        <section className="auth-form-panel" aria-label={`${formTitle} form`}>
          <header className="auth-form-header">
            <img
              className="auth-brand-logo"
              src={BLINGS_LOGO_URL}
              alt="Blings"
            />
            <h1>{formTitle}</h1>
          </header>
          <Authenticator {...props} components={components} />
        </section>
      )}
    </main>
  );
};

export default BrandedAuthenticator;
